import { apiFetch } from "../../lib/api";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import type { HighlighterCore } from "shiki";
import { createHighlighter } from "shiki";
import { FileTree, getExt, getExtColor } from "./FileTree";
import type { FileNode } from "./FileTree";

interface FilesWorkbenchProps {
  projectName: string;
}

const EXT_LANG: Record<string, string> = {
  rs: "Rust",
  ts: "TypeScript",
  tsx: "TypeScript React",
  js: "JavaScript",
  jsx: "JavaScript React",
  json: "JSON",
  toml: "TOML",
  yaml: "YAML",
  yml: "YAML",
  md: "Markdown",
  css: "CSS",
  html: "HTML",
  py: "Python",
  go: "Go",
  sh: "Shell Script",
  lock: "Lock File",
};

function getExtLang(name: string): string {
  return EXT_LANG[getExt(name)] || "Plain Text";
}

/* ── Shiki syntax highlighting ── */
const EXT_SHIKI_LANG: Record<string, string> = {
  rs: "rust", ts: "typescript", tsx: "tsx", js: "javascript", jsx: "jsx",
  json: "json", toml: "toml", yaml: "yaml", yml: "yaml", md: "markdown",
  css: "css", html: "html", py: "python", go: "go", sh: "bash",
  lock: "json", dockerfile: "dockerfile", sql: "sql", xml: "xml",
  svg: "xml", graphql: "graphql", c: "c", cpp: "cpp", h: "c", hpp: "cpp",
  java: "java", rb: "ruby", php: "php", swift: "swift", kt: "kotlin",
};

function getShikiLang(name: string): string | null {
  return EXT_SHIKI_LANG[getExt(name)] || null;
}

/** Map a TextMate scope to a syn-* CSS class */
function scopeToSynClass(scopes: string[]): string | null {
  for (let i = scopes.length - 1; i >= 0; i--) {
    const s = scopes[i];
    if (s.startsWith("comment")) return "syn-comment";
    if (s.startsWith("string") || s.startsWith("punctuation.definition.string")) return "syn-string";
    if (s.startsWith("keyword") || s.startsWith("storage.type") || s.startsWith("storage.modifier")) return "syn-keyword";
    if (s.startsWith("entity.name.function") || s.startsWith("support.function")) return "syn-fn";
    if (s.startsWith("entity.name.type") || s.startsWith("support.type") || s.startsWith("storage.type.class") || s.startsWith("entity.name.class")) return "syn-type";
    if (s.startsWith("constant.numeric")) return "syn-number";
    if (s.startsWith("constant")) return "syn-const";
    if (s.startsWith("entity.name.tag")) return "syn-tag";
    if (s.startsWith("entity.other.attribute-name")) return "syn-attr";
    if (s.startsWith("variable.other.property") || s.startsWith("support.type.property-name") || s.startsWith("meta.object-literal.key")) return "syn-prop";
    if (s.startsWith("punctuation")) return "syn-punct";
    if (s.startsWith("meta.attribute") || s.startsWith("entity.other.attribute")) return "syn-attr";
    if (s.startsWith("keyword.control.import") || s.startsWith("keyword.control.export")) return "syn-import";
  }
  return null;
}

type HighlightedLine = { cls: string | null; text: string }[];

let _highlighterPromise: Promise<HighlighterCore> | null = null;

function getOrCreateHighlighter(): Promise<HighlighterCore> {
  if (!_highlighterPromise) {
    _highlighterPromise = createHighlighter({
      themes: ["vitesse-dark"],
      langs: Object.values(EXT_SHIKI_LANG).filter((v, i, a) => a.indexOf(v) === i),
    });
  }
  return _highlighterPromise;
}

async function highlightCode(code: string, lang: string): Promise<HighlightedLine[]> {
  const highlighter = await getOrCreateHighlighter();
  const { tokens } = highlighter.codeToTokens(code, {
    lang,
    theme: "vitesse-dark",
    includeExplanation: "scopeName",
  });
  return tokens.map((line) =>
    line.map((token) => ({
      cls: token.explanation
        ? scopeToSynClass(token.explanation.flatMap((e) => e.scopes.map((s) => s.scopeName)))
        : null,
      text: token.content,
    }))
  );
}

/* ── Mock file tree ── */
function buildMockTree(): FileNode[] {
  return [
    {
      name: "src", path: "src", type: "dir", children: [
        {
          name: "bin", path: "src/bin", type: "dir", children: [
            { name: "opentool-web.rs", path: "src/bin/opentool-web.rs", type: "file" },
            { name: "opentool-admin.rs", path: "src/bin/opentool-admin.rs", type: "file" },
          ],
        },
        {
          name: "engine", path: "src/engine", type: "dir", children: [
            { name: "mod.rs", path: "src/engine/mod.rs", type: "file" },
            { name: "router.rs", path: "src/engine/router.rs", type: "file" },
            { name: "routing_policy.rs", path: "src/engine/routing_policy.rs", type: "file" },
          ],
        },
        { name: "lib.rs", path: "src/lib.rs", type: "file" },
        { name: "org.rs", path: "src/org.rs", type: "file" },
      ],
    },
    {
      name: "ui", path: "ui", type: "dir", children: [
        {
          name: "src", path: "ui/src", type: "dir", children: [
            { name: "App.tsx", path: "ui/src/App.tsx", type: "file" },
            {
              name: "components", path: "ui/src/components", type: "dir", children: [
                { name: "desktop", path: "ui/src/components/desktop", type: "dir", children: [] },
                { name: "chat", path: "ui/src/components/chat", type: "dir", children: [] },
                { name: "notebook", path: "ui/src/components/notebook", type: "dir", children: [] },
              ],
            },
          ],
        },
      ],
    },
    { name: "Cargo.toml", path: "Cargo.toml", type: "file" },
    { name: "package.json", path: "package.json", type: "file" },
    { name: "README.md", path: "README.md", type: "file" },
  ];
}

/* ── Mock file contents with syntax token spans ── */
type TokenSpan = { cls: string; text: string };
type MockLine = (string | TokenSpan)[];

function tok(cls: string, text: string): TokenSpan {
  return { cls, text };
}

const MOCK_CONTENTS: Record<string, { lines: MockLine[]; activeLine: number }> = {
  "src/bin/opentool-web.rs": {
    activeLine: 10,
    lines: [
      [tok("comment", "//! OpenTool Web — main HTTP entry point")],
      [],
      [tok("keyword", "use"), " std::net::", tok("type", "SocketAddr"), tok("punct", ";")],
      [tok("keyword", "use"), " axum::", tok("punct", "{"), tok("type", "Router"), tok("punct", ","), " routing::", tok("punct", "{"), tok("fn", "get"), tok("punct", ","), " ", tok("fn", "post"), tok("punct", "}};")],
      [tok("keyword", "use"), " tower_http::cors::", tok("type", "CorsLayer"), tok("punct", ";")],
      [],
      [tok("keyword", "mod"), " engine", tok("punct", ";")],
      [tok("keyword", "mod"), " org", tok("punct", ";")],
      [],
      [tok("macro", "#[tokio::main]")],
      [tok("keyword", "async"), " ", tok("keyword", "fn"), " ", tok("fn", "main"), tok("punct", "()"), " ", tok("punct", "->"), " ", tok("type", "Result"), tok("punct", "<"), tok("punct", "()"), tok("punct", ","), " ", tok("type", "Box"), tok("punct", "<"), tok("keyword", "dyn"), " std::error::", tok("type", "Error"), tok("punct", ">>"), " ", tok("punct", "{")],
      ["    ", tok("keyword", "let"), " app ", tok("punct", "="), " ", tok("type", "Router"), tok("punct", "::"), tok("fn", "new"), tok("punct", "()")],
      ["        ", tok("punct", "."), tok("fn", "route"), tok("punct", "("), tok("string", '"/health"'), tok("punct", ","), " ", tok("fn", "get"), tok("punct", "("), "health_check", tok("punct", "))"), ],
      ["        ", tok("punct", "."), tok("fn", "route"), tok("punct", "("), tok("string", '"/api/tools"'), tok("punct", ","), " ", tok("fn", "post"), tok("punct", "("), "engine::route_tool", tok("punct", "))"), ],
      ["        ", tok("punct", "."), tok("fn", "layer"), tok("punct", "("), tok("type", "CorsLayer"), tok("punct", "::"), tok("fn", "permissive"), tok("punct", "());")],
      [],
      ["    ", tok("keyword", "let"), " addr ", tok("punct", "="), " ", tok("type", "SocketAddr"), tok("punct", "::"), tok("fn", "from"), tok("punct", "("), tok("punct", "("), tok("punct", "["), tok("number", "0"), tok("punct", ","), " ", tok("number", "0"), tok("punct", ","), " ", tok("number", "0"), tok("punct", ","), " ", tok("number", "0"), tok("punct", "]"), tok("punct", ","), " ", tok("number", "8080"), tok("punct", "));")],
      ["    ", tok("fn", "println!"), tok("punct", "("), tok("string", '"listening on {addr}"'), tok("punct", ");")],
      ["    axum::", tok("type", "Server"), tok("punct", "::"), tok("fn", "bind"), tok("punct", "("), tok("punct", "&"), "addr", tok("punct", ")")],
      ["        ", tok("punct", "."), tok("fn", "serve"), tok("punct", "("), "app", tok("punct", "."), tok("fn", "into_make_service"), tok("punct", "())")],
      ["        ", tok("punct", "."), tok("keyword", "await"), tok("punct", "?;")],
      ["    ", tok("type", "Ok"), tok("punct", "("), tok("punct", "()"), tok("punct", ")")],
      [tok("punct", "}")],
      [],
      [tok("keyword", "async"), " ", tok("keyword", "fn"), " ", tok("fn", "health_check"), tok("punct", "()"), " ", tok("punct", "->"), " ", tok("punct", "&"), tok("keyword", "'static"), " ", tok("keyword", "str"), " ", tok("punct", "{")],
      ["    ", tok("string", '"ok"')],
      [tok("punct", "}")],
    ],
  },
  "src/engine/router.rs": {
    activeLine: 5,
    lines: [
      [tok("comment", "/// Tool routing engine — resolves tool calls to providers")],
      [],
      [tok("keyword", "use"), " serde::", tok("punct", "{"), tok("type", "Deserialize"), tok("punct", ","), " ", tok("type", "Serialize"), tok("punct", "};")],
      [tok("keyword", "use"), " crate::engine::routing_policy::", tok("type", "RoutingPolicy"), tok("punct", ";")],
      [],
      [tok("keyword", "pub"), " ", tok("keyword", "struct"), " ", tok("type", "ToolRouter"), " ", tok("punct", "{")],
      ["    policies: ", tok("type", "Vec"), tok("punct", "<"), tok("type", "RoutingPolicy"), tok("punct", ">"), tok("punct", ",")],
      ["    fallback: ", tok("type", "Option"), tok("punct", "<"), tok("type", "String"), tok("punct", ">"), tok("punct", ",")],
      [tok("punct", "}")],
      [],
      [tok("keyword", "impl"), " ", tok("type", "ToolRouter"), " ", tok("punct", "{")],
      ["    ", tok("keyword", "pub"), " ", tok("keyword", "fn"), " ", tok("fn", "new"), tok("punct", "()"), " ", tok("punct", "->"), " ", tok("keyword", "Self"), " ", tok("punct", "{")],
      ["        ", tok("keyword", "Self"), " ", tok("punct", "{"), " policies: ", tok("type", "Vec"), tok("punct", "::"), tok("fn", "new"), tok("punct", "()"), tok("punct", ","), " fallback: ", tok("type", "None"), " ", tok("punct", "}")],
      ["    ", tok("punct", "}")],
      [],
      ["    ", tok("keyword", "pub"), " ", tok("keyword", "async"), " ", tok("keyword", "fn"), " ", tok("fn", "route"), tok("punct", "("), tok("punct", "&"), tok("keyword", "self"), tok("punct", ","), " req: ", tok("punct", "&"), tok("type", "ToolRequest"), tok("punct", ")"), " ", tok("punct", "->"), " ", tok("type", "Result"), tok("punct", "<"), tok("type", "ToolResponse"), tok("punct", ">"), " ", tok("punct", "{")],
      ["        ", tok("comment", "// Evaluate policies in priority order")],
      ["        ", tok("keyword", "for"), " policy ", tok("keyword", "in"), " ", tok("punct", "&"), tok("keyword", "self"), tok("punct", "."), "policies ", tok("punct", "{")],
      ["            ", tok("keyword", "if"), " policy", tok("punct", "."), tok("fn", "matches"), tok("punct", "("), "req", tok("punct", ")"), " ", tok("punct", "{")],
      ["                ", tok("keyword", "return"), " policy", tok("punct", "."), tok("fn", "execute"), tok("punct", "("), "req", tok("punct", ")"), tok("punct", "."), tok("keyword", "await"), tok("punct", ";")],
      ["            ", tok("punct", "}")],
      ["        ", tok("punct", "}")],
      ["        ", tok("type", "Err"), tok("punct", "("), tok("fn", "anyhow!"), tok("punct", "("), tok("string", '"no matching policy"'), tok("punct", "))"),],
      ["    ", tok("punct", "}")],
      [tok("punct", "}")],
    ],
  },
  "ui/src/App.tsx": {
    activeLine: 7,
    lines: [
      [tok("import", "import"), " ", tok("type", "React"), " ", tok("import", "from"), " ", tok("string", "'react'"), tok("punct", ";")],
      [tok("import", "import"), " ", tok("punct", "{"), " ", tok("fn", "useState"), tok("punct", ","), " ", tok("fn", "useEffect"), " ", tok("punct", "}"), " ", tok("import", "from"), " ", tok("string", "'react'"), tok("punct", ";")],
      [tok("import", "import"), " ", tok("punct", "{"), " ", tok("type", "Desktop"), " ", tok("punct", "}"), " ", tok("import", "from"), " ", tok("string", "'./components/desktop/Desktop'"), tok("punct", ";")],
      [],
      [tok("keyword", "export"), " ", tok("keyword", "function"), " ", tok("fn", "App"), tok("punct", "()"), " ", tok("punct", "{")],
      ["  ", tok("keyword", "const"), " ", tok("punct", "["), "view", tok("punct", ","), " setView", tok("punct", "]"), " ", tok("punct", "="), " ", tok("fn", "useState"), tok("punct", "<"), tok("type", "string"), tok("punct", ">("), tok("string", "'desktop'"), tok("punct", ");")],
      [],
      ["  ", tok("keyword", "return"), " ", tok("punct", "(")],
      ["    ", tok("punct", "<"), tok("tag", "div"), " ", tok("attr", "className"), tok("punct", "="), tok("string", '"app-root"'), tok("punct", ">")],
      ["      ", tok("punct", "<"), tok("tag", "Desktop"), " ", tok("punct", "/>")],
      ["    ", tok("punct", "</"), tok("tag", "div"), tok("punct", ">")],
      ["  ", tok("punct", ");")],
      [tok("punct", "}")],
    ],
  },
  "Cargo.toml": {
    activeLine: 2,
    lines: [
      [tok("punct", "["), tok("keyword", "package"), tok("punct", "]")],
      [tok("attr", "name"), " ", tok("punct", "="), " ", tok("string", '"opentool"')],
      [tok("attr", "version"), " ", tok("punct", "="), " ", tok("string", '"0.1.0"')],
      [tok("attr", "edition"), " ", tok("punct", "="), " ", tok("string", '"2024"')],
      [],
      [tok("punct", "["), tok("keyword", "dependencies"), tok("punct", "]")],
      [tok("attr", "axum"), " ", tok("punct", "="), " ", tok("string", '"0.7"')],
      [tok("attr", "tokio"), " ", tok("punct", "="), " ", tok("punct", "{"), " ", tok("attr", "version"), " ", tok("punct", "="), " ", tok("string", '"1"'), tok("punct", ","), " ", tok("attr", "features"), " ", tok("punct", "="), " ", tok("punct", "["), tok("string", '"full"'), tok("punct", "]"), " ", tok("punct", "}")],
      [tok("attr", "serde"), " ", tok("punct", "="), " ", tok("punct", "{"), " ", tok("attr", "version"), " ", tok("punct", "="), " ", tok("string", '"1"'), tok("punct", ","), " ", tok("attr", "features"), " ", tok("punct", "="), " ", tok("punct", "["), tok("string", '"derive"'), tok("punct", "]"), " ", tok("punct", "}")],
      [tok("attr", "tower-http"), " ", tok("punct", "="), " ", tok("punct", "{"), " ", tok("attr", "version"), " ", tok("punct", "="), " ", tok("string", '"0.5"'), tok("punct", ","), " ", tok("attr", "features"), " ", tok("punct", "="), " ", tok("punct", "["), tok("string", '"cors"'), tok("punct", "]"), " ", tok("punct", "}")],
      [tok("attr", "anyhow"), " ", tok("punct", "="), " ", tok("string", '"1"')],
    ],
  },
  "package.json": {
    activeLine: 3,
    lines: [
      [tok("punct", "{")],
      ["  ", tok("attr", '"name"'), tok("punct", ":"), " ", tok("string", '"opentool-ui"'), tok("punct", ",")],
      ["  ", tok("attr", '"version"'), tok("punct", ":"), " ", tok("string", '"0.1.0"'), tok("punct", ",")],
      ["  ", tok("attr", '"private"'), tok("punct", ":"), " ", tok("const", "true"), tok("punct", ",")],
      ["  ", tok("attr", '"scripts"'), tok("punct", ":"), " ", tok("punct", "{")],
      ["    ", tok("attr", '"dev"'), tok("punct", ":"), " ", tok("string", '"vite"'), tok("punct", ",")],
      ["    ", tok("attr", '"build"'), tok("punct", ":"), " ", tok("string", '"tsc && vite build"'), tok("punct", ",")],
      ["    ", tok("attr", '"preview"'), tok("punct", ":"), " ", tok("string", '"vite preview"')],
      ["  ", tok("punct", "}"), tok("punct", ",")],
      ["  ", tok("attr", '"dependencies"'), tok("punct", ":"), " ", tok("punct", "{")],
      ["    ", tok("attr", '"react"'), tok("punct", ":"), " ", tok("string", '"^18.2.0"'), tok("punct", ",")],
      ["    ", tok("attr", '"react-dom"'), tok("punct", ":"), " ", tok("string", '"^18.2.0"')],
      ["  ", tok("punct", "}")],
      [tok("punct", "}")],
    ],
  },
};

/* ── Rendering helpers ── */
function renderTokenLine(line: MockLine): (string | React.ReactElement)[] {
  return line.map((part, i) => {
    if (typeof part === "string") return part;
    return <span key={i} className={`syn-${part.cls}`}>{part.text}</span>;
  });
}

/** @deprecated Use OpentoolCode instead */
export const FilesWorkbench = OpentoolCode;

export function OpentoolCode({ projectName }: FilesWorkbenchProps) {
  const [tree, setTree] = useState<FileNode[]>([]);
  const [activeFile, setActiveFile] = useState<string | null>(null);
  const [openTabs, setOpenTabs] = useState<string[]>([]);
  const [fileContent, setFileContent] = useState("");
  const [activeLine, setActiveLine] = useState(0);
  const [loading, setLoading] = useState(false);
  const [highlightedLines, setHighlightedLines] = useState<HighlightedLine[] | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const highlightGenRef = useRef(0);

  // Highlight file content with Shiki when it changes
  useEffect(() => {
    if (!activeFile || !fileContent || MOCK_CONTENTS[activeFile]) {
      setHighlightedLines(null);
      return;
    }
    const lang = getShikiLang(activeFile);
    if (!lang) {
      setHighlightedLines(null);
      return;
    }
    const gen = ++highlightGenRef.current;
    highlightCode(fileContent, lang).then((lines) => {
      if (gen === highlightGenRef.current) setHighlightedLines(lines);
    }).catch(() => {
      if (gen === highlightGenRef.current) setHighlightedLines(null);
    });
  }, [activeFile, fileContent]);

  // Fetch file tree from API, fall back to mock
  useEffect(() => {
    apiFetch("/api/files")
      .then((r) => {
        if (!r.ok) throw new Error("no api");
        return r.json();
      })
      .then((d) => {
        if (d.tree && d.tree.length > 0) setTree(d.tree);
        else setTree(buildMockTree());
      })
      .catch(() => setTree(buildMockTree()));
  }, []);

  // When active file changes, load content
  useEffect(() => {
    if (!activeFile) return;
    const mock = MOCK_CONTENTS[activeFile];
    if (mock) {
      setActiveLine(mock.activeLine);
      setLoading(false);
      return;
    }
    // Try API
    setLoading(true);
    setActiveLine(0);
    fetch(`/api/files/${activeFile}`)
      .then((r) => r.json())
      .then((d) => {
        setFileContent(d.content || "");
        setLoading(false);
      })
      .catch(() => {
        setFileContent("");
        setLoading(false);
      });
  }, [activeFile]);

  const openFile = useCallback(
    (path: string) => {
      setActiveFile(path);
      setOpenTabs((prev) => (prev.includes(path) ? prev : [...prev, path]));
    },
    []
  );

  const closeTab = useCallback(
    (path: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setOpenTabs((prev) => {
        const next = prev.filter((t) => t !== path);
        if (activeFile === path) {
          setActiveFile(next.length > 0 ? next[next.length - 1] : null);
        }
        return next;
      });
    },
    [activeFile]
  );

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => !prev);
  }, []);

  const breadcrumbs = activeFile ? activeFile.split("/") : [];
  const fileName = activeFile ? activeFile.split("/").pop() || "" : "";

  // Compute mock content lines
  const mockData = activeFile ? MOCK_CONTENTS[activeFile] : null;
  const totalLines = mockData ? mockData.lines.length : fileContent.split("\n").length;

  // Minimap blocks
  const minimapLines = useMemo(() => {
    if (!mockData) return [];
    return mockData.lines.map((line) => {
      if (line.length === 0) return { width: 0, color: "transparent" };
      // Estimate width from text length
      let len = 0;
      for (const part of line) {
        len += typeof part === "string" ? part.length : part.text.length;
      }
      // Color from first token
      const firstToken = line.find((p) => typeof p !== "string") as TokenSpan | undefined;
      let color = "rgba(200,200,230,0.3)";
      if (firstToken) {
        const colorMap: Record<string, string> = {
          comment: "rgba(106,153,85,0.5)",
          keyword: "rgba(167,139,250,0.5)",
          string: "rgba(206,145,120,0.5)",
          type: "rgba(56,189,248,0.4)",
          fn: "rgba(220,220,170,0.4)",
          macro: "rgba(56,189,248,0.5)",
          import: "rgba(167,139,250,0.4)",
          punct: "rgba(140,140,170,0.25)",
          attr: "rgba(156,220,254,0.3)",
        };
        color = colorMap[firstToken.cls] || color;
      }
      return { width: Math.min(len * 1.2, 48), color };
    });
  }, [mockData]);

  /* ── Code with line numbers (mock syntax) ── */
  const renderMockCode = () => {
    if (!mockData) return null;
    return (
      <div className="fe-code-lines">
        {mockData.lines.map((line, i) => (
          <div
            key={i}
            className={`fe-code-line${i === activeLine ? " fe-code-line--active" : ""}`}
            onClick={() => setActiveLine(i)}
          >
            <span className="fe-code-line__num">{i + 1}</span>
            <span className="fe-code-line__text">
              {line.length === 0 ? "\u00A0" : renderTokenLine(line)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  /* ── Highlighted or plain text code ── */
  const renderPlainCode = () => {
    if (highlightedLines) {
      return (
        <div className="fe-code-lines">
          {highlightedLines.map((tokens, i) => (
            <div
              key={i}
              className={`fe-code-line${i === activeLine ? " fe-code-line--active" : ""}`}
              onClick={() => setActiveLine(i)}
            >
              <span className="fe-code-line__num">{i + 1}</span>
              <span className="fe-code-line__text">
                {tokens.length === 0
                  ? "\u00A0"
                  : tokens.map((t, j) =>
                      t.cls ? (
                        <span key={j} className={t.cls}>{t.text}</span>
                      ) : (
                        <span key={j}>{t.text}</span>
                      )
                    )}
              </span>
            </div>
          ))}
        </div>
      );
    }
    const lines = fileContent.split("\n");
    return (
      <div className="fe-code-lines">
        {lines.map((line, i) => (
          <div
            key={i}
            className={`fe-code-line${i === activeLine ? " fe-code-line--active" : ""}`}
            onClick={() => setActiveLine(i)}
          >
            <span className="fe-code-line__num">{i + 1}</span>
            <span className="fe-code-line__text">{line || "\u00A0"}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="fe-layout">
      {/* ── File tree sidebar (collapsible) ── */}
      {!sidebarCollapsed && (
        <div className="fe-sidebar">
          <div className="fe-sidebar__header">
            <svg viewBox="0 0 24 24" className="fe-sidebar__header-icon">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <path d="M14 2v6h6" />
            </svg>
            <span>Explorer</span>
            <button
              className="fe-sidebar__toggle"
              onClick={toggleSidebar}
              title="Collapse sidebar"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="11 17 6 12 11 7" />
                <polyline points="18 17 13 12 18 7" />
              </svg>
            </button>
          </div>
          <div className="fe-sidebar__tree">
            {tree.length === 0 ? (
              <div className="fe-sidebar__empty">Loading...</div>
            ) : (
              <FileTree
                files={tree}
                onFileSelect={openFile}
                selectedPath={activeFile ?? undefined}
                defaultExpanded={new Set(["src", "src/bin", "src/engine", "ui", "ui/src"])}
              />
            )}
          </div>
        </div>
      )}
      {sidebarCollapsed && (
        <div className="fe-sidebar--collapsed">
          <button
            className="fe-sidebar__toggle fe-sidebar__toggle--expand"
            onClick={toggleSidebar}
            title="Expand sidebar"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="13 17 18 12 13 7" />
              <polyline points="6 17 11 12 6 7" />
            </svg>
          </button>
        </div>
      )}

      {/* ── Main editor panel ── */}
      <div className="fe-editor">
        {/* Tab bar */}
        {openTabs.length > 0 && (
          <div className="fe-tabs">
            {openTabs.map((tab) => {
              const tabName = tab.split("/").pop() || tab;
              return (
                <div
                  key={tab}
                  className={`fe-tab${tab === activeFile ? " fe-tab--active" : ""}`}
                  onClick={() => setActiveFile(tab)}
                >
                  <span className="fe-tab__dot" style={{ background: getExtColor(tabName) }} />
                  <span>{tabName}</span>
                  <span className="fe-tab__close" onClick={(e) => closeTab(tab, e)}>
                    &times;
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {activeFile ? (
          <>
            {/* Breadcrumbs */}
            <div className="fe-breadcrumbs">
              <svg viewBox="0 0 24 24" className="fe-breadcrumbs__icon">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <path d="M14 2v6h6" />
              </svg>
              {breadcrumbs.map((seg, i) => (
                <span key={i} className="fe-breadcrumbs__segment">
                  {i > 0 && <span className="fe-breadcrumbs__sep">{"\u203A"}</span>}
                  <span className={i === breadcrumbs.length - 1 ? "fe-breadcrumbs__current" : ""}>
                    {seg}
                  </span>
                </span>
              ))}
            </div>

            {/* Code content + minimap */}
            <div className="fe-editor__content">
              <div className="fe-editor__scroll">
                {loading ? (
                  <div className="fe-editor__loading">
                    <div className="fe-editor__loading-pulse" />
                    Loading...
                  </div>
                ) : mockData ? (
                  renderMockCode()
                ) : (
                  renderPlainCode()
                )}
              </div>

              {/* Minimap */}
              {mockData && (
                <div className="fe-minimap">
                  <div
                    className="fe-minimap__viewport"
                    style={{ top: 0, height: `${Math.min(100, (20 / (totalLines || 1)) * 100)}%` }}
                  />
                  {minimapLines.map((ml, i) => (
                    <div
                      key={i}
                      className="fe-minimap__line"
                      style={{ width: `${ml.width}px`, background: ml.color }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Status bar */}
            <div className="fe-statusbar">
              <div className="fe-statusbar__left">
                <span className="fe-statusbar__branch">
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M11.75 2.5a.75.75 0 100 1.5.75.75 0 000-1.5zm-2.116.776a2.252 2.252 0 01-.673 3.027A2.24 2.24 0 018 7.25v.5a2.25 2.25 0 002.25 2.25h.37a2.251 2.251 0 11-.006 1.5h-.364A3.75 3.75 0 016.5 7.75v-.5a.75.75 0 00-.75-.75A2.25 2.25 0 013.5 4.25a2.25 2.25 0 014.134-1.224A2.24 2.24 0 019.634 3.276zM5.75 2.5a.75.75 0 100 1.5.75.75 0 000-1.5zm5.25 9.5a.75.75 0 100 1.5.75.75 0 000-1.5z" />
                  </svg>
                  main
                </span>
              </div>
              <div className="fe-statusbar__right">
                <span className="fe-statusbar__item">
                  Ln {activeLine + 1}, Col 1
                </span>
                <span className="fe-statusbar__item">Spaces: 4</span>
                <span className="fe-statusbar__item">UTF-8</span>
                <span className="fe-statusbar__item">{getExtLang(fileName)}</span>
              </div>
            </div>
          </>
        ) : (
          <div className="fe-editor__empty">
            <svg viewBox="0 0 24 24" className="fe-editor__empty-icon">
              <polyline points="16 18 22 12 16 6" />
              <polyline points="8 6 2 12 8 18" />
            </svg>
            <span>Select a file to begin editing</span>
            <div className="fe-editor__empty-keys">
              <div className="fe-editor__empty-key"><kbd>Ctrl+P</kbd> Quick Open File</div>
              <div className="fe-editor__empty-key"><kbd>Ctrl+Shift+E</kbd> Toggle Explorer</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
